import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { syncAccountBalance } from '../../../lib/accountBalance';
import {
  CalendarEvent,
  EVENT_TYPE_COLORS,
  EVENT_TYPE_ICONS,
  EVENT_TYPE_LABELS,
} from '../../../hooks/useCalendarEvents';

interface EventDetailModalProps {
  open: boolean;
  event: CalendarEvent | null;
  googleConnected?: boolean;
  onClose: () => void;
  onEdit: (event: CalendarEvent) => void;
  onDelete: (
    id: string,
    mode: 'single' | 'future' | 'all',
    recurrenceGroupId?: string | null,
    startDate?: string
  ) => Promise<{ success: boolean; message: string }>;
  onSync?: (event: CalendarEvent) => Promise<{ success: boolean; message: string }>;
  onPayCard?: (cardId: string | null | undefined, cardName: string | null | undefined, type: 'full' | 'partial') => void;
  onGoalContribution?: (goalId: string | null | undefined, goalName: string | null | undefined) => void;
  onActionComplete?: () => void;
}

// ─── Tradução das recorrências para PT-BR ─────────────────────────────────────
const RECURRENCE_PT: Record<string, string> = {
  daily:     'Diário',
  weekly:    'Semanal',
  biweekly:  'Quinzenal',
  monthly:   'Mensal',
  annual:    'Anual',
  yearly:    'Anual',
};

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  pago: { label: 'Pago', cls: 'bg-emerald-100 text-emerald-700' },
  recebido: { label: 'Recebido', cls: 'bg-emerald-100 text-emerald-700' },
  pendente: { label: 'Pendente', cls: 'bg-amber-100 text-amber-700' },
  vencido: { label: 'Vencido', cls: 'bg-red-100 text-red-600' },
  atrasado: { label: 'Atrasado', cls: 'bg-red-100 text-red-600' },
  active: { label: 'Ativo', cls: 'bg-gray-100 text-gray-600' },
  done: { label: 'Concluído', cls: 'bg-emerald-100 text-emerald-700' },
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
};

const SOURCE_MODULE_LABELS: Record<string, string> = {
  transactions: 'Lançamentos',
  cards: 'Cartões',
  goals: 'Metas',
};

const SOURCE_ROUTES: Record<string, string> = {
  transactions: '/lancamentos',
  cards: '/cartoes',
  goals: '/metas',
};

// ─── Contextual Action Config ────────────────────────────────────────────────
type ContextualAction = {
  id: string;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
};

const getContextualActions = (event: CalendarEvent): ContextualAction[] => {
  const actions: ContextualAction[] = [];

  const alreadyDone =
    event.status === 'pago' ||
    event.status === 'recebido' ||
    event.status === 'done';

  const isPayable =
    event.status === 'pendente' ||
    event.status === 'vencido' ||
    event.status === 'atrasado' ||
    event.status === 'active' ||
    event.status == null ||
    event.status === '';

  switch (event.event_type) {
    case 'card_due':
      if (!alreadyDone) {
        actions.push({
          id: 'pay_full',
          label: 'Pagar fatura',
          icon: 'ri-money-dollar-circle-line',
          color: 'text-white',
          bgColor: 'bg-emerald-500 hover:bg-emerald-600',
        });
        actions.push({
          id: 'pay_partial',
          label: 'Pagamento parcial',
          icon: 'ri-percent-line',
          color: 'text-gray-700',
          bgColor: 'bg-white hover:bg-gray-50 border border-gray-200',
        });
      }
      break;

    case 'card_closing':
      break;

    case 'bill_due':
    case 'recurring_transaction':
    case 'installment':
    case 'payment_reminder':
      if (!alreadyDone && isPayable && event.linked_transaction_id) {
        actions.push({
          id: 'mark_paid',
          label: 'Marcar como pago',
          icon: 'ri-check-double-line',
          color: 'text-white',
          bgColor: 'bg-emerald-500 hover:bg-emerald-600',
        });
      }
      if (!alreadyDone && event.linked_transaction_id) {
        actions.push({
          id: 'reschedule',
          label: 'Adiar para outra data',
          icon: 'ri-calendar-line',
          color: 'text-gray-700',
          bgColor: 'bg-white hover:bg-gray-50 border border-gray-200',
        });
      }
      break;

    case 'income_expected':
      if (!alreadyDone && isPayable && event.linked_transaction_id) {
        actions.push({
          id: 'confirm_income',
          label: 'Confirmar recebimento',
          icon: 'ri-checkbox-circle-line',
          color: 'text-white',
          bgColor: 'bg-emerald-600 hover:bg-emerald-700',
        });
        actions.push({
          id: 'reschedule',
          label: 'Adiar para outra data',
          icon: 'ri-calendar-line',
          color: 'text-gray-700',
          bgColor: 'bg-white hover:bg-gray-50 border border-gray-200',
        });
      }
      break;

    case 'income_received':
      break;

    case 'goal_deadline':
      actions.push({
        id: 'goal_contribution',
        label: 'Fazer aporte à meta',
        icon: 'ri-funds-line',
        color: 'text-white',
        bgColor: 'bg-emerald-500 hover:bg-emerald-600',
      });
      break;

    case 'manual_note':
      if (!alreadyDone) {
        actions.push({
          id: 'mark_done',
          label: 'Marcar como concluído',
          icon: 'ri-checkbox-circle-line',
          color: 'text-white',
          bgColor: 'bg-gray-700 hover:bg-gray-800',
        });
        actions.push({
          id: 'reschedule',
          label: 'Adiar para outra data',
          icon: 'ri-calendar-line',
          color: 'text-gray-700',
          bgColor: 'bg-white hover:bg-gray-50 border border-gray-200',
        });
      }
      break;

    default:
      break;
  }

  // "Ver origem" para eventos automáticos com módulo de origem
  if (!event.is_manual && event.source_module) {
    actions.push({
      id: 'view_source',
      label: `Ver em ${SOURCE_MODULE_LABELS[event.source_module] ?? event.source_module}`,
      icon:
        event.source_module === 'transactions' ? 'ri-exchange-dollar-line' :
        event.source_module === 'cards' ? 'ri-bank-card-line' :
        event.source_module === 'goals' ? 'ri-focus-3-line' :
        'ri-external-link-line',
      color: 'text-gray-500',
      bgColor: 'bg-gray-50 hover:bg-gray-100 border border-gray-200',
    });
  }

  return actions;
};

export const EventDetailModal = ({
  open,
  event,
  googleConnected,
  onClose,
  onEdit,
  onDelete,
  onSync,
  onPayCard,
  onGoalContribution,
  onActionComplete,
}: EventDetailModalProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [deleteMode, setDeleteMode] = useState<null | 'options'>(null);
  const [deleting, setDeleting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  // ── Editar valor de ocorrência individual ──────────────────────────────────
  const [editAmountOpen, setEditAmountOpen] = useState(false);
  const [editAmountValue, setEditAmountValue] = useState('');
  const [editAmountSaving, setEditAmountSaving] = useState(false);

  // ── Reset de estado ao trocar de evento ───────────────────────────────────
  useEffect(() => {
    setDeleteMode(null);
    setFeedback(null);
    setShowReschedule(false);
    setRescheduleDate('');
    setActionLoading(null);
    setEditAmountOpen(false);
    setEditAmountValue(String(event?.amount ?? ''));
  }, [event?.id]);

  if (!open || !event) return null;

  const icon = EVENT_TYPE_ICONS[event.event_type] ?? 'ri-calendar-line';
  const typeLabel = EVENT_TYPE_LABELS[event.event_type] ?? 'Evento';
  const color = event.color || EVENT_TYPE_COLORS[event.event_type];
  const statusInfo = STATUS_LABEL[event.status] ?? { label: event.status, cls: 'bg-gray-100 text-gray-600' };
  const isIncome = event.event_type === 'income_expected' || event.event_type === 'income_received';
  const contextualActions = getContextualActions(event);

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minRescheduleDate = tomorrow.toISOString().split('T')[0];

  const handleDelete = async (mode: 'single' | 'future' | 'all') => {
    setDeleting(true);
    const result = await onDelete(event.id, mode, event.recurrence_group_id, event.start_date);
    setDeleting(false);
    if (result.success) onClose();
    else setFeedback({ type: 'error', msg: result.message });
  };

  const handleSync = async () => {
    if (!onSync) return;
    setSyncing(true);
    const result = await onSync(event);
    setSyncing(false);
    setFeedback({ type: result.success ? 'success' : 'error', msg: result.message });
  };

  const handleMarkTransactionPaid = async (actionId: string) => {
    if (!event.linked_transaction_id || !user) return;
    setActionLoading(actionId);
    const nextStatus = actionId === 'confirm_income' ? 'recebido' : 'pago';
    const today = new Date().toISOString().split('T')[0];
    const { error } = await supabase
      .from('transactions')
      .update({ status: nextStatus, paid_at: today, updated_at: new Date().toISOString() })
      .eq('id', event.linked_transaction_id)
      .eq('user_id', user.id);
    if (!error) {
      await syncAccountBalance(event.linked_account_id ?? null);
      setFeedback({
        type: 'success',
        msg: actionId === 'confirm_income' ? 'Recebimento confirmado!' : 'Lançamento marcado como pago!',
      });
      onActionComplete?.();
    } else {
      setFeedback({ type: 'error', msg: 'Erro ao atualizar lançamento.' });
    }
    setActionLoading(null);
  };

  const handleMarkDone = async () => {
    if (!user) return;
    setActionLoading('mark_done');
    const { error } = await supabase
      .from('calendar_events')
      .update({ status: 'done', updated_at: new Date().toISOString() })
      .eq('id', event.id)
      .eq('user_id', user.id);
    if (!error) {
      setFeedback({ type: 'success', msg: 'Evento marcado como concluído!' });
      onActionComplete?.();
    } else {
      setFeedback({ type: 'error', msg: 'Erro ao atualizar evento.' });
    }
    setActionLoading(null);
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !user) return;
    setRescheduling(true);

    if (event.linked_transaction_id) {
      const { error } = await supabase
        .from('transactions')
        .update({ date: rescheduleDate, updated_at: new Date().toISOString() })
        .eq('id', event.linked_transaction_id)
        .eq('user_id', user.id);
      if (!error) {
        setFeedback({ type: 'success', msg: 'Data adiada com sucesso!' });
        setShowReschedule(false);
        onActionComplete?.();
      } else {
        setFeedback({ type: 'error', msg: 'Erro ao adiar o lançamento.' });
      }
    } else if (event.is_manual) {
      const { error } = await supabase
        .from('calendar_events')
        .update({ start_date: rescheduleDate, updated_at: new Date().toISOString() })
        .eq('id', event.id)
        .eq('user_id', user.id);
      if (!error) {
        setFeedback({ type: 'success', msg: 'Evento adiado com sucesso!' });
        setShowReschedule(false);
        onActionComplete?.();
      } else {
        setFeedback({ type: 'error', msg: 'Erro ao adiar o evento.' });
      }
    }
    setRescheduling(false);
  };

  // ── Editar valor desta ocorrência individualmente ─────────────────────────
  const handleEditOccurrenceAmount = async () => {
    if (!event.linked_transaction_id || !user) return;
    const newAmount = parseFloat(editAmountValue.replace(',', '.'));
    if (isNaN(newAmount) || newAmount <= 0) return;
    setEditAmountSaving(true);
    const { error } = await supabase
      .from('transactions')
      .update({ amount: newAmount, updated_at: new Date().toISOString() })
      .eq('id', event.linked_transaction_id)
      .eq('user_id', user.id);
    if (!error) {
      setFeedback({ type: 'success', msg: 'Valor desta ocorrência atualizado com sucesso!' });
      setEditAmountOpen(false);
      onActionComplete?.();
    } else {
      setFeedback({ type: 'error', msg: 'Erro ao atualizar o valor.' });
    }
    setEditAmountSaving(false);
  };

  const handleContextualAction = (actionId: string) => {
    switch (actionId) {
      case 'pay_full':
        onPayCard?.(event.linked_credit_card_id, event.card_name, 'full');
        onClose();
        break;
      case 'pay_partial':
        onPayCard?.(event.linked_credit_card_id, event.card_name, 'partial');
        onClose();
        break;
      case 'mark_paid':
        handleMarkTransactionPaid('mark_paid');
        break;
      case 'confirm_income':
        handleMarkTransactionPaid('confirm_income');
        break;
      case 'goal_contribution':
        onGoalContribution?.(event.linked_goal_id, event.goal_name);
        onClose();
        break;
      case 'mark_done':
        handleMarkDone();
        break;
      case 'reschedule':
        setRescheduleDate(minRescheduleDate);
        setShowReschedule(true);
        break;
      case 'view_source': {
        const route = SOURCE_ROUTES[event.source_module ?? ''];
        if (route) {
          navigate(route);
          onClose();
        }
        break;
      }
      default:
        break;
    }
  };

  const canDelete =
    event.is_manual ||
    event.event_type === 'installment' ||
    event.event_type === 'recurring_transaction' ||
    event.event_type === 'bill_due' ||
    event.event_type === 'income_expected';

  // Rótulo correto por tipo de evento
  const deleteSingleLabel = (() => {
    if (event.is_manual) return 'Somente este evento';
    if (event.event_type === 'installment') return 'Somente esta parcela';
    if (event.event_type === 'recurring_transaction') return 'Somente esta ocorrência';
    if (event.event_type === 'income_expected') return 'Somente esta receita';
    return 'Somente este lançamento';
  })();

  const deleteFutureLabel = (() => {
    if (event.is_manual) return 'Este e os próximos';
    if (event.event_type === 'income_expected') return 'Esta e as próximas a receber';
    return 'Esta e as próximas pendentes';
  })();

  const deleteAllLabel = (() => {
    if (event.event_type === 'installment') return 'Toda a série de parcelas';
    if (event.event_type === 'recurring_transaction') return 'Toda a recorrência';
    if (event.event_type === 'income_expected') return 'Toda a série de receitas';
    return 'Toda a série';
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md shadow-lg overflow-hidden">
        {/* Color top bar */}
        <div className="h-1.5 w-full" style={{ backgroundColor: color }} />

        {/* Header */}
        <div className="flex items-start gap-3 px-5 pt-4 pb-3 border-b border-gray-100">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ backgroundColor: color + '20' }}
          >
            <i className={`${icon} text-[18px]`} style={{ color }} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-[16px] font-700 text-gray-900 leading-snug">{event.title}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span
                className="text-[11px] px-2 py-0.5 rounded-full font-600"
                style={{ backgroundColor: color + '18', color }}
              >
                {typeLabel}
              </span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-500 ${statusInfo.cls}`}>
                {statusInfo.label}
              </span>
              {event.is_manual && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-500">
                  Manual
                </span>
              )}
              {event.sync_with_google && (
                <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-50 text-red-500 font-500 flex items-center gap-1">
                  <i className="ri-google-line text-[10px]" />
                  Google
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Date */}
          <div className="flex items-center gap-2.5 py-2.5 px-3 bg-gray-50 rounded-xl">
            <div className="w-7 h-7 flex items-center justify-center">
              <i className="ri-calendar-event-line text-gray-400 text-[16px]" />
            </div>
            <div>
              <p className="text-[11px] text-gray-400 font-500">
                {event.paid_at ? 'Vencimento / Competência' : 'Data'}
              </p>
              <p className="text-[13px] font-600 text-gray-800 capitalize">{formatDate(event.start_date)}</p>
            </div>
          </div>

          {/* Paid at — só exibe quando existe e é diferente do vencimento */}
          {event.paid_at && event.paid_at !== event.start_date && (
            <div className="flex items-center gap-2.5 py-2.5 px-3 bg-emerald-50 rounded-xl border border-emerald-100">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-emerald-500 text-[16px]" />
              </div>
              <div>
                <p className="text-[11px] text-emerald-600 font-500">Pago em</p>
                <p className="text-[13px] font-600 text-emerald-800 capitalize">{formatDate(event.paid_at)}</p>
              </div>
            </div>
          )}

          {/* Paid at — quando coincide com vencimento, só mostra se já pago (confirma a data) */}
          {event.paid_at && event.paid_at === event.start_date && (event.status === 'pago' || event.status === 'recebido') && (
            <div className="flex items-center gap-1.5 px-3 py-1.5">
              <i className="ri-check-line text-emerald-500 text-[12px]" />
              <p className="text-[11px] text-emerald-600">
                {event.status === 'recebido' ? 'Recebido' : 'Pago'} na data de vencimento
              </p>
            </div>
          )}

          {/* Amount */}
          {event.amount != null && (
            <div className="flex items-center gap-2.5 py-2.5 px-3 rounded-xl" style={{ backgroundColor: color + '10' }}>
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-[16px]" style={{ color }} />
              </div>
              <div className="flex-1">
                <p className="text-[11px] text-gray-400 font-500">{isIncome ? 'Valor recebido / previsto' : 'Valor'}</p>
                <p className="text-[15px] font-700" style={{ color }}>
                  {isIncome ? '+' : '-'}
                  {Number(event.amount).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </p>
              </div>
              {/* Botão de editar valor (apenas para recorrências) */}
              {event.event_type === 'recurring_transaction' && event.linked_transaction_id && !editAmountOpen && (
                <button
                  onClick={() => setEditAmountOpen(true)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-500 text-gray-500 hover:bg-white/60 cursor-pointer whitespace-nowrap transition-colors"
                  title="Editar valor desta ocorrência"
                >
                  <i className="ri-edit-line text-[12px]" />
                  Editar
                </button>
              )}
            </div>
          )}

          {/* Painel de editar valor da ocorrência */}
          {editAmountOpen && event.event_type === 'recurring_transaction' && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl space-y-3">
              <p className="text-[12px] font-700 text-emerald-800 flex items-center gap-1.5">
                <i className="ri-edit-line text-[13px]" />
                Editar valor desta ocorrência
              </p>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={editAmountValue}
                onChange={(e) => setEditAmountValue(e.target.value)}
                className="w-full px-3 py-2 border border-emerald-300 rounded-lg text-[13px] text-gray-800 focus:outline-none focus:border-emerald-500 bg-white"
                placeholder="0,00"
              />
              <p className="text-[11px] text-emerald-700">
                Apenas esta ocorrência será alterada. As demais mantêm o valor original.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setEditAmountOpen(false)}
                  className="flex-1 py-2 text-[12px] font-500 text-gray-500 bg-white border border-gray-200 rounded-lg cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleEditOccurrenceAmount}
                  disabled={editAmountSaving || !editAmountValue}
                  className="flex-1 py-2 text-[12px] font-600 text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-1"
                >
                  {editAmountSaving
                    ? <><i className="ri-loader-4-line animate-spin text-[12px]" />Salvando...</>
                    : 'Confirmar'}
                </button>
              </div>
            </div>
          )}

          {/* Description */}
          {event.description && (
            <div className="flex items-start gap-2.5 py-2.5 px-3 bg-gray-50 rounded-xl">
              <div className="w-7 h-7 flex items-center justify-center mt-0.5">
                <i className="ri-file-text-line text-gray-400 text-[15px]" />
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500">Descrição</p>
                <p className="text-[13px] text-gray-700">{event.description}</p>
              </div>
            </div>
          )}

          {/* Account */}
          {event.account_name && (
            <div className="flex items-center gap-2.5 py-2.5 px-3 bg-gray-50 rounded-xl">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-bank-line text-gray-400 text-[15px]" />
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500">Conta</p>
                <p className="text-[13px] font-600 text-gray-800">{event.account_name}</p>
              </div>
            </div>
          )}

          {/* Card */}
          {event.card_name && (
            <div className="flex items-center gap-2.5 py-2.5 px-3 bg-gray-50 rounded-xl">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-bank-card-line text-gray-400 text-[15px]" />
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500">Cartão</p>
                <p className="text-[13px] font-600 text-gray-800">{event.card_name}</p>
              </div>
            </div>
          )}

          {/* Goal */}
          {event.goal_name && (
            <div className="flex items-center gap-2.5 py-2.5 px-3 bg-gray-50 rounded-xl">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-focus-3-line text-gray-400 text-[15px]" />
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500">Meta vinculada</p>
                <p className="text-[13px] font-600 text-gray-800">{event.goal_name}</p>
              </div>
            </div>
          )}

          {/* Category */}
          {event.category && (
            <div className="flex items-center gap-2.5 py-2.5 px-3 bg-gray-50 rounded-xl">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-price-tag-3-line text-gray-400 text-[15px]" />
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500">Categoria</p>
                <p className="text-[13px] font-600 text-gray-800">{event.category}</p>
              </div>
            </div>
          )}

          {/* Recurrence — traduzido para PT-BR */}
          {event.recurrence && event.recurrence !== 'none' && (
            <div className="flex items-center gap-2.5 py-2.5 px-3 bg-gray-50 rounded-xl">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-repeat-line text-gray-400 text-[15px]" />
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500">Recorrência</p>
                <p className="text-[13px] font-600 text-gray-800">
                  {RECURRENCE_PT[event.recurrence] ?? event.recurrence}
                </p>
              </div>
            </div>
          )}

          {/* Source */}
          {event.source_module && (
            <div className="flex items-center gap-2.5 py-2.5 px-3 bg-gray-50 rounded-xl">
              <div className="w-7 h-7 flex items-center justify-center">
                <i className="ri-database-2-line text-gray-400 text-[15px]" />
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500">Origem</p>
                <p className="text-[13px] font-600 text-gray-800">
                  {SOURCE_MODULE_LABELS[event.source_module] ?? event.source_module}
                  {' '}• Gerado automaticamente
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          {event.notes && (
            <div className="flex items-start gap-2.5 py-2.5 px-3 bg-gray-50 rounded-xl">
              <div className="w-7 h-7 flex items-center justify-center mt-0.5">
                <i className="ri-sticky-note-line text-gray-400 text-[15px]" />
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500">Observações</p>
                <p className="text-[13px] text-gray-700">{event.notes}</p>
              </div>
            </div>
          )}

          {/* ─── Ações Contextuais ─── */}
          {contextualActions.length > 0 && (
            <div className="pt-1">
              <p className="text-[11px] font-600 text-gray-400 mb-2 uppercase tracking-wide">Ações</p>
              <div className="flex flex-col gap-2">
                {contextualActions.map(action => (
                  <button
                    key={action.id}
                    onClick={() => handleContextualAction(action.id)}
                    disabled={actionLoading === action.id}
                    className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-[13px] font-600 cursor-pointer transition-colors w-full justify-center ${action.bgColor} ${action.color}`}
                  >
                    {actionLoading === action.id ? (
                      <i className="ri-loader-4-line animate-spin text-[14px]" />
                    ) : (
                      <i className={`${action.icon} text-[14px]`} />
                    )}
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ─── Painel de adiar ─── */}
          {showReschedule && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
              <p className="text-[12px] font-700 text-amber-800 flex items-center gap-1.5">
                <i className="ri-calendar-line text-[14px]" />
                Adiar para nova data
              </p>
              <input
                type="date"
                value={rescheduleDate}
                min={minRescheduleDate}
                onChange={(e) => setRescheduleDate(e.target.value)}
                className="w-full px-3 py-2 border border-amber-300 rounded-lg text-[13px] text-gray-800 focus:outline-none focus:border-amber-400 bg-white"
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowReschedule(false)}
                  className="flex-1 px-3 py-2 text-[12px] font-500 text-gray-500 bg-white border border-gray-200 rounded-lg cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleReschedule}
                  disabled={rescheduling || !rescheduleDate}
                  className="flex-1 px-3 py-2 text-[12px] font-600 text-white bg-amber-500 hover:bg-amber-600 rounded-lg cursor-pointer disabled:opacity-50 whitespace-nowrap"
                >
                  {rescheduling ? 'Adiando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          )}

          {/* Feedback */}
          {feedback && (
            <div className={`px-3 py-2.5 rounded-xl text-[12px] font-500 ${
              feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'
            }`}>
              <i className={`${feedback.type === 'success' ? 'ri-check-line' : 'ri-error-warning-line'} mr-1.5`} />
              {feedback.msg}
            </div>
          )}

          {/* ─── Opções de exclusão ─── */}
          {deleteMode === 'options' && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-xl space-y-2">
              <p className="text-[12px] font-600 text-red-700 mb-2">
                {event.is_manual ? 'Remover do calendário:' : 'Excluir lançamento:'}
              </p>

              {/* Opção 1: Somente esta ocorrência */}
              <button
                onClick={() => handleDelete('single')}
                disabled={deleting}
                className="w-full px-3 py-2 text-[12px] font-500 text-left bg-white border border-red-200 rounded-lg text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-close-line mr-2" />
                {deleteSingleLabel}
              </button>

              {/* Opções 2 e 3: mostrar para recorrências e parcelamentos */}
              {(
                event.recurrence_group_id ||
                event.event_type === 'recurring_transaction' ||
                event.event_type === 'installment' ||
                event.event_type === 'income_expected' ||
                (event.recurrence && event.recurrence !== 'none')
              ) && (
                <>
                  {/* Opção 2: Esta e as próximas pendentes */}
                  {(
                    event.is_manual ||
                    event.event_type === 'recurring_transaction' ||
                    event.event_type === 'income_expected' ||
                    (event.recurrence && event.recurrence !== 'none')
                  ) && (
                    <button
                      onClick={() => handleDelete('future')}
                      disabled={deleting}
                      className="w-full px-3 py-2 text-[12px] font-500 text-left bg-white border border-red-200 rounded-lg text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-right-line mr-2" />
                      {deleteFutureLabel}
                    </button>
                  )}

                  {/* Opção 3: Excluir toda a série */}
                  <button
                    onClick={() => handleDelete('all')}
                    disabled={deleting}
                    className="w-full px-3 py-2 text-[12px] font-500 text-left bg-white border border-red-200 rounded-lg text-red-600 hover:bg-red-50 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line mr-2" />
                    {deleteAllLabel}
                  </button>
                </>
              )}

              <button
                onClick={() => setDeleteMode(null)}
                className="w-full px-3 py-1.5 text-[12px] font-500 text-gray-500 cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center gap-2 flex-wrap">
          {googleConnected && onSync && (
            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-[12px] font-500 text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              {syncing ? (
                <i className="ri-loader-4-line animate-spin text-[13px]" />
              ) : (
                <i className="ri-google-line text-[13px]" />
              )}
              {event.sync_with_google ? 'Ressincronizar' : 'Enviar ao Google'}
            </button>
          )}

          <div className="flex-1" />

          {canDelete && deleteMode !== 'options' && (
            <button
              onClick={() => setDeleteMode('options')}
              className="px-3 py-2 text-[12px] font-500 text-red-500 hover:bg-red-50 border border-red-200 rounded-lg cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line mr-1" />
              {event.is_manual ? 'Remover' : 'Excluir'}
            </button>
          )}
          {event.is_manual && (
            <button
              onClick={() => onEdit(event)}
              className="px-3 py-2 text-[12px] font-600 text-gray-700 hover:bg-gray-50 border border-gray-200 rounded-lg cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-2-line mr-1" />
              Editar
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-100 text-[13px] font-600 text-gray-700 hover:bg-gray-200 cursor-pointer whitespace-nowrap"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
